import type { DraftRow } from './useWeightDraft.js';

/**
 * How the targets list is filtered and ordered for display.
 *
 * Kept out of the component, and pure, because this is the part with rules
 * worth pinning down: which rows a query matches, what "sort by percent" does
 * to two rows with the same weight, and — the one that actually bites — that
 * neither operation may lose a row's position in the draft. The weight editor
 * is an editor: every row it shows has to be able to say which row of the
 * DRAFT it is, or a sorted view would write an edit to the wrong destination
 * and the colour dots would stop matching the split bar above them.
 *
 * So the unit here is a row plus its draft index, never a bare row.
 */

/** Which rows are shown, by their enabled flag. */
export type TargetStatusFilter = 'all' | 'live' | 'paused';

export type TargetSort =
  /** The draft's own order — newest first, since a new row is prepended. */
  | 'order'
  | 'percent-desc'
  | 'percent-asc'
  | 'url-asc';

export type TargetView = { query: string; status: TargetStatusFilter; sort: TargetSort };

export const DEFAULT_TARGET_VIEW: TargetView = { query: '', status: 'all', sort: 'order' };

/** A draft row and where it sits in the draft, which is its identity here. */
export type PositionedRow = { row: DraftRow; index: number };

export function matchesTargetQuery(row: DraftRow, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase();
  if (query === '') return true;
  return row.url.toLowerCase().includes(query);
}

/**
 * The rows to render, in the order to render them.
 *
 * Sorting is stable on the draft order, so rows that tie — two destinations
 * both at 25%, which is the common case rather than the exotic one — keep the
 * order the operator put them in rather than shuffling on every keystroke.
 */
export function viewTargets(rows: readonly DraftRow[], view: TargetView): PositionedRow[] {
  const positioned: PositionedRow[] = rows.map((row, index) => ({ row, index }));

  const filtered = positioned.filter(({ row }) => {
    if (view.status === 'live' && !row.enabled) return false;
    if (view.status === 'paused' && row.enabled) return false;
    return matchesTargetQuery(row, view.query);
  });

  if (view.sort === 'order') return filtered;

  return [...filtered].sort((left, right) => {
    const byField =
      view.sort === 'url-asc'
        ? left.row.url.localeCompare(right.row.url)
        : view.sort === 'percent-desc'
          ? right.row.weightBp - left.row.weightBp
          : left.row.weightBp - right.row.weightBp;
    return byField !== 0 ? byField : left.index - right.index;
  });
}
